import { useMemo, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

import { useAuth } from '@/hooks/useAuth'
import { useNotification, Card, Button, Input, GoogleAuthButton } from '@/components/common'
import { isGoogleOAuthConfigured } from '@/utils/googleAuth'

// ─── Schema ────────────────────────────────────────────────────────────────
const registerSchema = z
  .object({
    username: z
      .string()
      .min(3, 'Tên đăng nhập phải có ít nhất 3 ký tự')
      .max(20, 'Tên đăng nhập không được vượt quá 20 ký tự')
      .regex(/^[a-zA-Z0-9_]+$/, 'Chỉ được dùng chữ cái, số và dấu gạch dưới'),
    email: z.string().email('Email không hợp lệ').max(100, 'Email không được vượt quá 100 ký tự'),
    password: z
      .string()
      .min(8, 'Mật khẩu phải có ít nhất 8 ký tự')
      .max(128, 'Mật khẩu không được vượt quá 128 ký tự')
      .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/, 'Mật khẩu cần có chữ hoa, chữ thường và số'),
    confirmPassword: z.string().min(1, 'Vui lòng xác nhận mật khẩu'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Mật khẩu xác nhận không khớp',
    path: ['confirmPassword'],
  })

// ─── Divider ───────────────────────────────────────────────────────────────
const Divider = () => (
  <div className="relative py-2">
    <div className="absolute inset-0 flex items-center">
      <div className="w-full border-t border-gray-200" />
    </div>
    <div className="relative flex justify-center">
      <span className="bg-white px-3 text-xs text-gray-500">HOẶC</span>
    </div>
  </div>
)

// ─── Page ──────────────────────────────────────────────────────────────────
const RegisterPage = () => {
  const navigate = useNavigate()
  const { register: registerUser, googleAuth, loading, error, clearError } = useAuth()
  const { showNotification } = useNotification()

  const defaultValues = useMemo(
    () => ({ username: '', email: '', password: '', confirmPassword: '' }),
    []
  )

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues,
    mode: 'onTouched',
  })

  const busy = loading || isSubmitting
  const googleEnabled = isGoogleOAuthConfigured()

  const notifySuccess = useCallback(
    (title, message) => showNotification({ type: 'success', title, message, duration: 3000 }),
    [showNotification]
  )

  const notifyError = useCallback(
    (title, message) => showNotification({ type: 'error', title, message, duration: 4000 }),
    [showNotification]
  )

  const onSubmit = useCallback(
    async (data) => {
      try {
        clearError?.()
        await registerUser({
          username: data.username,
          email: data.email,
          password: data.password,
        })
        notifySuccess('Đăng ký thành công!', `Chào mừng ${data.username} đến với WebChess!`)
        navigate('/dashboard', { replace: true })
      } catch (err) {
        notifyError('Đăng ký thất bại', err?.message || 'Vui lòng thử lại.')
      }
    },
    [clearError, registerUser, notifySuccess, notifyError, navigate]
  )

  const onGoogleRegister = useCallback(async (credential) => {
    try {
      clearError?.()
      const user = await googleAuth(credential)
      notifySuccess(
        'Google đăng ký thành công',
        `Chào mừng ${user?.displayName || user?.username || 'bạn'} đến với WebChess!`
      )
      navigate('/dashboard', { replace: true })
    } catch (err) {
      notifyError('Google đăng ký thất bại', err?.message || 'Vui lòng thử lại.')
    }
  }, [clearError, googleAuth, navigate, notifyError, notifySuccess])

  return (
    <div className="min-h-[100dvh] bg-[#e1edff] flex items-center justify-center px-4 py-6">
      <div className="w-full max-w-5xl flex flex-col lg:flex-row items-center justify-between gap-6 sm:gap-10 lg:gap-8">
        {/* LEFT */}
        <div className="w-full lg:w-1/2 text-center lg:text-left">
          <div className="mb-4">
            <span className="text-6xl text-blue-600 leading-none select-none">♟</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-blue-600 tracking-tight mb-3 sm:mb-4">
            WebChess
          </h1>
          <p className="text-lg sm:text-2xl font-medium text-gray-800 leading-tight max-w-md mx-auto lg:mx-0">
            Tham gia cộng đồng hàng triệu kỳ thủ. Miễn phí mãi mãi.
          </p>
        </div>

        {/* RIGHT */}
        <div className="w-full lg:w-[420px]">
          <Card
            variant="elevated"
            padding="none"
            className="bg-white shadow-md border-none rounded-xl overflow-hidden"
          >
            <div className="p-5 sm:p-8">
              <div className="mb-5">
                <h2 className="text-2xl font-bold text-blue-400">Tạo tài khoản</h2>
                <p className="text-sm text-gray-500 mt-1">Miễn phí và chỉ mất 1 phút.</p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-100 text-red-600 rounded-lg text-sm font-medium">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                <Input
                  placeholder="Tên đăng nhập"
                  fullWidth
                  {...register('username')}
                  error={errors.username?.message}
                  disabled={busy}
                  className="py-3 !border-gray-600 !text-black placeholder:!text-gray-400 focus:!border-blue-400 focus:!ring-0 transition-none"
                />

                <Input
                  type="email"
                  placeholder="Email"
                  fullWidth
                  {...register('email')}
                  error={errors.email?.message}
                  disabled={busy}
                  className="py-3  !border-gray-600 !text-black placeholder:!text-gray-400 focus:!border-blue-400 focus:!ring-0 transition-none"
                />

                <Input
                  type="password"
                  placeholder="Mật khẩu (ít nhất 8 ký tự)"
                  fullWidth
                  {...register('password')}
                  error={errors.password?.message}
                  disabled={busy}
                  className="py-3  !border-gray-600 !text-black placeholder:!text-gray-400 focus:!border-blue-400 focus:!ring-0 transition-none"
                />

                <Input
                  type="password"
                  placeholder="Xác nhận mật khẩu"
                  fullWidth
                  {...register('confirmPassword')}
                  error={errors.confirmPassword?.message}
                  disabled={busy}
                  className="py-3  !border-gray-600 !text-black placeholder:!text-gray-400 focus:!border-blue-400 focus:!ring-0 transition-none"
                />

                <Button
                  type="submit"
                  variant="primary"
                  fullWidth
                  size="lg"
                  loading={busy}
                  className="py-3 text-lg font-bold bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm mt-1"
                >
                  Đăng ký
                </Button>

                {googleEnabled ? (
                  <>
                    <Divider />
                    <GoogleAuthButton
                      onCredential={onGoogleRegister}
                      disabled={busy}
                      text="signup_with"
                    />
                  </>
                ) : null}

                <div className="pt-5 mt-3 border-t border-gray-100 text-center">
                  <p className="text-sm text-gray-600">
                    Đã có tài khoản?{' '}
                    <Link to="/login" className="text-blue-400 font-bold hover:underline ml-1">
                      Đăng nhập ngay
                    </Link>
                  </p>
                </div>
              </form>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default RegisterPage
